/**
 * 도면 대조 — 층의 원본 DXF 에서 다시 뽑은 기본 객체(주차면·CCTV·비상벨·기둥)와 지금 맵의 객체를
 * 종류별로 맞춰 본다. 수량만이 아니라 "도면에는 있는데 맵에 없는 자리"와 "맵에만 있는 객체"를 짚어
 * 바로 찾아가 고치게 한다.
 *
 * 좌표: 도면 객체는 층 CSV 좌표(미터), 맵 객체는 그래프 좌표다. 가져오기의 배율·Y 뒤집기는 저장되지
 * 않으므로, 층 CSV 에서 온 맵 객체(entityHandle 이 CSV 와 같은 것)를 짝으로 삼아 변환을 맞춘다.
 */
import { BASIC_OBJECT_CATEGORIES, basicObjectCategory } from '../../csv/lib/basicObjectExtraction'
import type { ParsedEntity } from '../../csv/lib/csvParser'
import {
  applyTransform,
  fitSimilarityFromPairs,
  MIN_SIMILARITY_PAIRS,
  type Pt,
  type SimilarityTransform,
} from '../../zone-auto-import/lib/transform'

export type CheckKind = (typeof BASIC_OBJECT_CATEGORIES)[number]['key']

/** 백엔드 /dxf-parser/drawing-objects 의 객체 하나(층 CSV 좌표, 미터). */
export interface DrawingObject {
  kind: CheckKind
  layer: string
  x: number
  y: number
}

/** 맵(그래프) 객체 하나 — 좌표는 그래프 좌표. */
export interface MapObject {
  id: string
  kind: CheckKind
  handle?: string
  x: number
  y: number
}

/** 층 CSV 객체의 중심(미터) — 맵 객체와 같은 handle 이면 변환을 맞추는 짝이 된다. */
export interface CsvAnchor {
  handle: string
  x: number
  y: number
}

export interface KindResult {
  kind: CheckKind
  label: string
  drawing: number
  map: number
  /** 도면에는 있는데 근처에 맵 객체가 없는 자리(그래프 좌표). */
  missing: Pt[]
  /** 근처에 도면 객체가 없는 맵 객체. */
  extra: MapObject[]
}

/**
 * 같은 객체로 칠 거리(m). 주차칸 중심은 이웃 칸과 2.3m 이상 떨어지고, 기둥은 8m 격자다.
 * 맵에서 손으로 조금 옮긴 것까지는 같은 객체로 본다.
 */
export const MATCH_TOLERANCE_M: Record<CheckKind, number> = {
  parking: 1.2,
  cctv: 1.5,
  emergency: 1.5,
  pillar: 0.8,
}

/** 맵 셀 카테고리(routeLayerToCategory) → 대조 종류. */
export function kindOfCategory(category: string): CheckKind | null {
  switch (category) {
    case 'parkingLocation':
      return 'parking'
    case 'cctv':
    case 'warningCctv':
      return 'cctv'
    case 'emergencyBell':
      return 'emergency'
    case 'column':
      return 'pillar'
    default:
      return null
  }
}

/** 점들의 중심 — 닫힌 도형의 마지막 꼭짓점(첫 점 반복)은 뺀다. 백엔드 readDrawingObjects 와 같은 규칙. */
function centroid(points: Pt[]): Pt {
  const n = points.length
  const last = points[n - 1]
  const pts = n > 1 && points[0].x === last.x && points[0].y === last.y ? points.slice(0, -1) : points
  const sum = pts.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 })
  return { x: sum.x / pts.length, y: sum.y / pts.length }
}

/** 층 CSV 의 기본 객체마다 handle·중심. */
export function csvAnchors(entities: readonly ParsedEntity[]): CsvAnchor[] {
  return entities
    .filter((e) => e.points.length > 0 && basicObjectCategory(e.layer))
    .map((e) => ({ handle: e.entityHandle, ...centroid(e.points) }))
}

/**
 * 층 CSV(미터) → 그래프 좌표 변환. 층 CSV 에서 온 맵 객체가 MIN_SIMILARITY_PAIRS 개 미만이면 null —
 * 이때는 위치를 못 맞추니 수량만 비교한다.
 */
export function fitCsvToMap(
  anchors: readonly CsvAnchor[],
  mapObjects: readonly MapObject[],
): SimilarityTransform | null {
  const byHandle = new Map(anchors.map((a) => [a.handle, a]))
  const pairs = mapObjects.flatMap((m) => {
    const a = m.handle ? byHandle.get(m.handle) : undefined
    return a ? [{ pdf: { x: a.x, y: a.y }, editor: { x: m.x, y: m.y } }] : []
  })
  if (pairs.length < MIN_SIMILARITY_PAIRS) return null
  return fitSimilarityFromPairs(pairs)
}

function toMeters(t: SimilarityTransform, p: Pt): Pt {
  return { x: (p.x - t.tx) / t.s, y: (p.y - t.ty) / (t.s * t.fy) }
}

/** 거리 tol 안의 짝을 가까운 순으로 하나씩 맺고, 짝 없는 도면·맵 인덱스를 돌려준다. */
function unmatched(drawing: Pt[], map: Pt[], tol: number): { drawing: number[]; map: number[] } {
  const candidates: [number, number, number][] = []
  // ponytail: 종류별 O(n·m) — 한 층 주차면 2천 개면 4백만 번 거리 계산(수십 ms), 더 크면 격자 버킷.
  drawing.forEach((d, i) => {
    map.forEach((m, j) => {
      const dist = Math.hypot(d.x - m.x, d.y - m.y)
      if (dist <= tol) candidates.push([dist, i, j])
    })
  })
  candidates.sort((a, b) => a[0] - b[0])
  const usedD = new Set<number>()
  const usedM = new Set<number>()
  for (const [, i, j] of candidates) {
    if (usedD.has(i) || usedM.has(j)) continue
    usedD.add(i)
    usedM.add(j)
  }
  return {
    drawing: drawing.map((_, i) => i).filter((i) => !usedD.has(i)),
    map: map.map((_, j) => j).filter((j) => !usedM.has(j)),
  }
}

/** 종류별 도면·맵 수량과, 변환이 있으면 짝 없는 자리. */
export function compareDrawing(
  drawing: readonly DrawingObject[],
  mapObjects: readonly MapObject[],
  transform: SimilarityTransform | null,
): KindResult[] {
  return BASIC_OBJECT_CATEGORIES.map(({ key, label }) => {
    const d = drawing.filter((o) => o.kind === key)
    const m = mapObjects.filter((o) => o.kind === key)
    if (!transform) return { kind: key, label, drawing: d.length, map: m.length, missing: [], extra: [] }
    const left = unmatched(
      d,
      m.map((o) => toMeters(transform, o)),
      MATCH_TOLERANCE_M[key],
    )
    return {
      kind: key,
      label,
      drawing: d.length,
      map: m.length,
      missing: left.drawing.map((i) => applyTransform(transform, d[i])),
      extra: left.map.map((j) => m[j]),
    }
  })
}
